import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ComicCard from "@/components/base/ComicCard";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import { comics } from "@/mocks/comics";
import { categories } from "@/mocks/categories";

export default function Home() {
  const hotComics = [...comics].sort((a, b) => b.views - a.views).slice(0, 8);
  const latestComics = [...comics].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()).slice(0, 10);
  const rankComics = [...comics].sort((a, b) => b.rating - a.rating).slice(0, 10);
  const [activeTab, setActiveTab] = useState<"hot" | "latest">("hot");

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              name: "4399漫画网",
              url: "https://www.4399mh.com",
              description: "4399漫画网提供无删减韩国漫画下拉式阅读，免费韩漫大全资源齐全多样",
              contactPoint: {
                "@type": "ContactPoint",
                email: "contact@4399mh.com",
                contactType: "customer service",
                availableLanguage: ["Chinese"],
              },
            },
            {
              "@context": "https://schema.org",
              "@type": "WebSite",
              name: "4399漫画网",
              url: "https://www.4399mh.com",
              description: "免费韩漫大全 - 无删减韩国漫画下拉式阅读平台",
              potentialAction: {
                "@type": "SearchAction",
                target: "https://www.4399mh.com/search?q={search_term_string}",
                "query-input": "required name=search_term_string",
              },
            },
            {
              "@context": "https://schema.org",
              "@type": "WebPage",
              name: "4399漫画网 - 首页",
              description: "韩国漫画无删减下拉式阅读，免费韩漫大全",
              url: "https://www.4399mh.com",
              isPartOf: { "@type": "WebSite", name: "4399漫画网", url: "https://www.4399mh.com" },
            },
          ]),
        }}
      />
      <Navbar />

      <main>
        <HeroCarousel />

        {/* Category Navigation */}
        <section className="bg-background-50 py-8">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                漫画分类
              </h2>
              <Link
                to="/category/all"
                className="text-sm text-foreground-500 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {categories.filter(c => c.id !== "all").map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center gap-2 p-4 rounded-xl bg-background-100 hover:bg-primary-50 border border-transparent hover:border-primary-200 transition-all duration-200"
                >
                  <div className="w-11 h-11 rounded-xl bg-primary-100 flex items-center justify-center group-hover:bg-primary-500 transition-colors duration-200">
                    <i className={`${cat.icon} text-lg text-primary-500 group-hover:text-background-50 transition-colors duration-200`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 transition-colors duration-200 whitespace-nowrap">
                    {cat.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot & Latest Tabs */}
        <section className="bg-background-100/70 py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2 bg-background-200/60 rounded-full p-1">
                <button
                  onClick={() => setActiveTab("hot")}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeTab === "hot"
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-500 hover:text-foreground-700"
                  }`}
                >
                  热门推荐
                </button>
                <button
                  onClick={() => setActiveTab("latest")}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeTab === "latest"
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-500 hover:text-foreground-700"
                  }`}
                >
                  最新更新
                </button>
              </div>
              <Link
                to="/category/all"
                className="text-sm text-foreground-500 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
              >
                更多 <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {(activeTab === "hot" ? hotComics : latestComics).map((comic, idx) => (
                <ComicCard key={comic.id} comic={comic} rank={activeTab === "hot" ? idx + 1 : undefined} />
              ))}
            </div>
          </div>
        </section>

        {/* Rankings */}
        <section className="bg-background-50 py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                人气排行榜
              </h2>
              <Link
                to="/category/all"
                className="text-sm text-foreground-500 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="bg-background-100 rounded-2xl p-5 md:p-8">
              <div className="space-y-3">
                {rankComics.map((comic, index) => (
                  <Link
                    key={comic.id}
                    to={`/article/${comic.id}`}
                    className="flex items-center gap-4 p-3 rounded-xl hover:bg-background-50 transition-colors duration-200 group"
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                      index < 3
                        ? "bg-accent-500 text-background-50"
                        : "bg-background-200 text-foreground-400"
                    }`}>
                      {index + 1}
                    </div>
                    <div className="w-12 h-16 rounded-lg overflow-hidden flex-shrink-0">
                      <img src={comic.cover} alt={comic.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors duration-200">
                        {comic.title}
                      </h3>
                      <p className="text-xs text-foreground-400 mt-0.5">{comic.author} · {comic.chapters}话</p>
                    </div>
                    <div className="hidden sm:flex flex-wrap gap-1.5">
                      {comic.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-700 text-xs whitespace-nowrap">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="flex items-center gap-1">
                        <i className="ri-star-fill text-accent-500 text-xs"></i>
                        <span className="text-sm font-medium text-foreground-700">{comic.rating}</span>
                      </div>
                      <div className="text-xs text-foreground-400 hidden md:block">
                        {comic.views > 10000 ? `${(comic.views / 10000).toFixed(0)}万阅` : `${comic.views}阅`}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="bg-background-100/70 py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-8">
              为什么选择4399漫画网
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {[
                { icon: "ri-shield-check-line", title: "正版无删减", desc: "所有韩漫均为正版授权无删减版本，完整呈现原作精彩内容" },
                { icon: "ri-download-2-line", title: "下拉式阅读", desc: "创新的下拉式阅读体验，流畅翻页，沉浸式享受每一话" },
                { icon: "ri-flashlight-line", title: "极速更新", desc: "同步韩国最新连载进度，第一时间为您带来最新话" },
                { icon: "ri-smartphone-line", title: "全平台适配", desc: "PC、平板、手机全平台完美适配，随时随地畅享韩漫" },
              ].map((feature) => (
                <div key={feature.title} className="p-5 rounded-xl bg-background-50 border border-background-200/60 text-center">
                  <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mx-auto mb-3">
                    <i className={`${feature.icon} text-xl text-primary-500`}></i>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}